import React, { createContext, useContext, useState, useEffect } from 'react';
import ReactMarkdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { Languages } from 'lucide-react';
import PlaylistPlayer from './PlaylistPlayer';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// --- GLOBAL STYLE PATCH --------------------------------------------------
// (can be moved to your tailwind/global.css, keeping here for clarity)
const superscriptCSS = `
  .superscript-link {
    font-size: 0.85rem;
    line-height: 1;      /* avoids extra vertical gap               */
    cursor: pointer;
    color: #1d4ed8;
    text-decoration: none;
    font-weight: bold;
  }
`;
// -------------------------------------------------------------------------

// ===================================================================================
// INTERFACES
// ===================================================================================

interface Footnote {
  snippet: string;
  sourceName: string;
}

interface MessageComponentProps {
  msg: {
    text: string;
    isUser: boolean;
    isLoading?: boolean;
    footnotes?: Record<string, Footnote>;
  };
  handleSendMessage: (text: string) => void;
  messageIndex: number;
  translationEnabled: boolean;
  mode: 'citation' | 'normal';
}

// ===================================================================================
// SELF-CONTAINED TOOLTIP COMPONENT
// ===================================================================================

const TooltipContent: React.FC<{ footnote: Footnote; translationEnabled: boolean }> = ({ footnote, translationEnabled }) => {
  const [translationState, setTranslationState] = useState({
    loading: false,
    translatedText: '',
    isTranslated: false,
    showingTranslated: false,
  });

  const cleanSnippet = (snippet: string): string => {
    return snippet
      .replace(/\\[nr]/g, ' ')
      .replace(/ (copyright|author|email|phone)\b:?\s*\S+/gi, '')
      .replace(/\S+@\S+\.\S+/g, '')
      .replace(/(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, '');
  };

  const snippet = cleanSnippet(footnote.snippet);
  const displayText = translationState.showingTranslated ? translationState.translatedText : snippet;

  const handleTranslate = async (e: React.MouseEvent) => {
    // ✅ FIX: Prevent click event from bubbling up to parent superscript
    e.stopPropagation();
    
    if (translationState.isTranslated) {
      setTranslationState(prev => ({ ...prev, showingTranslated: !prev.showingTranslated }));
      return;
    }

    setTranslationState(prev => ({ ...prev, loading: true }));

    try {
      const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
      const response = await fetch(`${API_BASE_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: snippet, target_lang: 'en' }),
      });
      if (!response.ok) throw new Error('Translation failed');
      const data = await response.json();
      setTranslationState({
        loading: false,
        translatedText: data.translated_text,
        isTranslated: true,
        showingTranslated: true,
      });
    } catch (error) {
      console.error(error);
      setTranslationState({
        loading: false,
        translatedText: 'Error translating.',
        isTranslated: true,
        showingTranslated: true,
      });
    }
  };

  const toggleDisplay = (e: React.MouseEvent) => {
    // ✅ FIX: Prevent click event from bubbling up to parent superscript
    e.stopPropagation();
    setTranslationState(prev => ({ ...prev, showingTranslated: !prev.showingTranslated }));
  };

  return (
    <div className="source-tooltip p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
         style={{ maxWidth: '450px', pointerEvents: 'auto', textAlign: 'left' }}>
      <div className="max-h-64 overflow-y-auto pr-2" style={{ userSelect: 'text' }}>
        <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{displayText}"</p>
      </div>
      <div className="flex justify-between items-center mt-2">
        <strong className="text-xs text-gray-500 dark:text-gray-400" style={{ userSelect: 'text' }}>
          {footnote.sourceName}
        </strong>
        {translationEnabled && (
          <button
            onClick={translationState.isTranslated ? toggleDisplay : handleTranslate}
            disabled={translationState.loading}
            className={`ml-2 p-1 rounded transition-colors ${
              translationState.loading
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
            }`}
            title={
              translationState.isTranslated
                ? translationState.showingTranslated
                  ? 'Show original text'
                  : 'Show translated text'
                : 'Translate to English'
            }
          >
            {translationState.loading ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            ) : (
              <Languages className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};


// ===================================================================================
// STABLE MARKDOWN RENDERERS
// ===================================================================================

interface MessageMarkdownContextValue {
  footnotes: MessageComponentProps['msg']['footnotes'];
  handleSendMessage: MessageComponentProps['handleSendMessage'];
  onSupClick: (e: React.MouseEvent<HTMLSpanElement>, n: string) => void;
  translationEnabled: boolean;
}

const MessageMarkdownContext = createContext<MessageMarkdownContextValue | null>(null);

function useMessageMarkdownContext() {
  const context = useContext(MessageMarkdownContext);
  if (!context) throw new Error('Message Markdown requires its message context');
  return context;
}

// Keep component types stable while context supplies the current message and actions.
const AnchorRenderer = ({ node: _node, ...props }: any) => {
  const { handleSendMessage } = useMessageMarkdownContext();
  const { href = '' } = props as { href?: string };
  if (href === '#') {
    const text = props.children?.toString() || '';
    return (
      <span
        onClick={e => {
          e.preventDefault();
          handleSendMessage('Elaborate comprehensively about: ' + text);
        }}
        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1d4ed8' }}
      >
        {props.children}
      </span>
    );
  }
  return <a {...props} />;
};

const messageMarkdownComponents: Components = {
  a: AnchorRenderer,
  u: AnchorRenderer,
  pre: ({ node: _node, ...props }) => <pre className="whitespace-pre-wrap" {...props} />,
  code({ node: _node, inline, className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & ExtraProps & { inline?: boolean }) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <SyntaxHighlighter style={a11yDark} language={match[1]} PreTag="div" {...props}>
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>{children}</code>
    );
  },
  sup: function MessageSuperscript({ node: _node, ...props }: any) {
    const { footnotes, onSupClick, translationEnabled } = useMessageMarkdownContext();
    const id = props.id || '';
    const n = id.split('-').pop()!;
    const footnote = footnotes?.[n];
    const SupInner = (
      <span {...props} className="superscript-link" onClick={(e) => onSupClick(e, n)}>
        {props.children}
      </span>
    );

    if (!footnote) return <sup {...props} />;

    return (
      <Tippy
        content={<TooltipContent
          key={JSON.stringify([footnote.sourceName, footnote.snippet])}
          footnote={footnote}
          translationEnabled={translationEnabled}
        />}
        interactive
        placement="top"
        appendTo={() => document.body}
        delay={[250, 100]}
      >
        {SupInner}
      </Tippy>
    );
  },
};

// ===================================================================================
// MAIN MESSAGE COMPONENT
// ===================================================================================

const MessageComponent: React.FC<MessageComponentProps> = ({
  msg,
  handleSendMessage,
  messageIndex,
  translationEnabled,
  mode,
}) => {
  const { text, footnotes = {} } = msg;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('superscript-style')) return;
    const style = document.createElement('style');
    style.id = 'superscript-style';
    style.innerHTML = superscriptCSS;
    document.head.appendChild(style);
  }, []);

  // -------------------------------------------------------------
  //  HELPER FUNCTIONS FOR TEXT PROCESSING
  // -------------------------------------------------------------
  // ✅ drop-in replacement
  const cleanText = (rawText: string): string => {
    // 1️⃣ Was this chunk sent as an SSE data line?
    if (rawText.startsWith('data: ')) {
      const jsonStr = rawText.substring(6).trim();   // remove "data: "
      try {
        // 2️⃣ If it's valid JSON, show only the answer
        const parsed = JSON.parse(jsonStr);
        return parsed.answer ?? rawText;
      } catch {
        // 3️⃣ Not JSON? – then it was plain text prefixed with "data: "
        return jsonStr;
      }
    }
    // 4️⃣ Non-SSE line (normal / listening chunk) → return unchanged
    return rawText;
  };

  const unfoldArrayPayload = (raw: string): string => {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.every(item => typeof item === 'string')) {
        return arr.join('\n');
      }
    } catch { /* not an array – ignore */ }
    return raw;
  };

  const normalizeHtmlFragments = (src: string): string => {
    let out = src.replace(
      /<u>(.*?)<\/u>/gi,
      (_m, inner) => `[${inner.trim()}](#)`
    );
    out = out.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_m, inner) => {
      const items = inner
        .split(/<\/?li[^>]*>/gi)
        .map(s => s.trim())
        .filter(Boolean);
      return items.map(s => `- ${s}`).join('\n');
    });
    return out;
  };

  const splitFootnoteGroup = (s: string): string => {
    return s.replace(/\[\^([^\]]+?)\]/g, (_m, inner) => {
      const parts = inner
        .split(',')
        .map(p => p.trim().replace(/^\^/, ''))
        .filter(Boolean);
      return parts.length <= 1 ? _m : parts.map(p => `[^${p}]`).join('');
    });
  };

  const processCitations = (text: string, messageIndex: number, mode: 'citation' | 'normal'): string => {
    let normalizedText = text
      .replace(/\[\s*\^?([\d,\s]+)\s*\]/g, (_m, content) =>
        content.split(/[,\s]+/).filter(Boolean).map(n => `[^${n}]`).join(''))
      .replace(/(\[\^\d+\])\s+(\[\^\d+\])/g, '$1$2');

    return normalizedText.replace(/\[\^(\d+)\]/g, (_match, n) =>
      mode === 'normal'
        ? `<sup id="cite-ref-${messageIndex}-${n}" class="superscript-link" data-n="${n}">${n}</sup>`
        : `<sup id="cite-ref-${messageIndex}-${n}" class="superscript-link" data-n="${n}">[${n}]</sup>`
    );
  };

  // -----------------------------------------------------------------
  // PROCESSING PIPELINE
  // -----------------------------------------------------------------
  const _core = (raw: string, mode: 'citation' | 'normal') =>
    processCitations(
      splitFootnoteGroup(normalizeHtmlFragments(unfoldArrayPayload(cleanText(raw)))),
      messageIndex,
      mode
    );

  // ✅ FIX #2: Removed call to the buggy `attachFootnoteDefs` function.
  const prepareCitation = (raw: string) => _core(raw, 'citation');
  const prepareNormal = (raw: string) => _core(raw, 'normal');

  // SUPERSCRIPT CLICK: allow infinite toggling by neutralising native hash
  const onSupClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, n: string) => {
    e.preventDefault();            // stop native jump
    const targetId = `fn-${messageIndex}-${n}`;
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // keep URL hash accurate so readers can copy links / refresh
      history.replaceState(null, '', `#${targetId}`);
    }
  };

  const processedText = mode === 'citation'
      ? prepareCitation(text)
      : prepareNormal(text);

  const normalDefs = (mode === 'normal')
    ? processedText.match(/\n\[\^\d+]:[\s\S]*/)?.[0] ?? ''
    : '';

  const renderContentWithAudio = (content: string) => {
    const safeContent = mode === 'citation'
        ? prepareCitation(content)
        : prepareNormal(content.includes('\n[^') ? content : `${content}\n\n${normalDefs}`);
        
    const mediaRegex = /\[MEDIA:(\d+):(audio|video)\](?:\s*([^\n\[]+))?/g;
    const mediaItems: { id: string; type: 'audio' | 'video'; title: string }[] = [];
    const textParts: string[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mediaRegex.exec(safeContent)) !== null) {
      const [fullMatch, id, type, rawTitle] = match;
      if (match.index > lastIndex) {
        textParts.push(safeContent.substring(lastIndex, match.index));
      }
      mediaItems.push({
        id,
        type: type as 'audio' | 'video',
        title: (rawTitle || `${type.charAt(0).toUpperCase() + type.slice(1)} ${id}`).trim(),
      });
      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < safeContent.length) {
      textParts.push(safeContent.substring(lastIndex));
    }

    return (
      <>
        {textParts.map((part, index) => (
          part.trim() && (
            <ReactMarkdown
              key={`text-${index}`}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={messageMarkdownComponents}
            >
              {mode === 'citation'
                ? prepareCitation(part)
                : prepareNormal(part.includes('\n[^') ? part : `${part}\n\n${normalDefs}`)
              }
            </ReactMarkdown>
          )
        ))}
        {mediaItems.length > 0 && (
          <PlaylistPlayer
            key={`playlist-${messageIndex}`}
            items={mediaItems}
            playlistId={`playlist-${messageIndex}`}
          />
        )}
      </>
    );
  };

  const [translations, setTranslations] = useState<Record<string, { originalText: string; translatedText: string; loading: boolean; translated: boolean; targetLang?: string; showingTranslated: boolean }>>({});
  const [supportedLanguages, setSupportedLanguages] = useState<Array<{ code: string; name: string }>>([]);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);
  const [lockedLanguageSelections, setLockedLanguageSelections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (translationEnabled && !languagesLoaded) {
      fetchSupportedLanguages();
    }
  }, [translationEnabled, languagesLoaded]);

  const fetchSupportedLanguages = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
      const response = await fetch(`${API_BASE_URL}/api/translate/languages`);
      if (response.ok) {
        const data = await response.json();
        const languages = data.languages || [];
        const sortedLanguages = languages.sort((a, b) => {
          if (a.name === 'English') return -1;
          if (b.name === 'English') return 1;
          return a.name.localeCompare(b.name);
        });
        setSupportedLanguages(sortedLanguages);
        setLanguagesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching supported languages:', error);
    }
  };
  
  const cleanSnippet = (snippet: string): string => {
    return snippet
      .replace(/\\[nr]/g, ' ')
      .replace(/ (copyright|author|email|phone)\b:?\s*\S+/gi, '')
      .replace(/\S+@\S+\.\S+/g, '')
      .replace(/(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, '');
  };

  const handleFootnoteListTranslate = async (key: string, textToTranslate: string, targetLang: string = 'en') => {
    if (translations[key]?.translated) return;

    setTranslations(prev => ({
      ...prev,
      [key]: { originalText: textToTranslate, translatedText: '', loading: true, translated: false, targetLang, showingTranslated: false }
    }));

    try {
      const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
      const response = await fetch(`${API_BASE_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToTranslate, target_lang: targetLang }),
      });
      if (!response.ok) throw new Error('Translation failed');
      const data = await response.json();
      setTranslations(prev => ({
        ...prev,
        [key]: { ...prev[key], translatedText: data.translated_text, loading: false, translated: true, showingTranslated: true }
      }));
      setLockedLanguageSelections(prev => ({ ...prev, [key]: true }));
    } catch (error) {
      console.error(error);
      setTranslations(prev => ({
        ...prev,
        [key]: { ...prev[key], translatedText: 'Error translating.', loading: false, translated: true, showingTranslated: true }
      }));
    }
  };

  const toggleFootnoteListTranslation = (key: string) => {
    setTranslations(prev => ({
      ...prev,
      [key]: { ...prev[key], showingTranslated: !prev[key]?.showingTranslated }
    }));
  };

  const renderFootnotesList = () => {
    if (!footnotes || Object.keys(footnotes).length === 0) return null;

    const handleBacklinkClick = (e: React.MouseEvent, targetId: string) => {
      e.preventDefault();
      // Reset hash if it matches the targetId to ensure subsequent clicks work
      if (window.location.hash === `#${targetId}`) {
        history.replaceState(null, '', ' ');
      }
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    return (
      <>
        <hr className="my-4 border-gray-300 dark:border-gray-600" />
        <ol className="text-sm list-decimal list-inside space-y-2">
          {Object.entries(footnotes).map(([key, value]) => {
            const footnoteKey = `footnote-${messageIndex}-${key}`;
            const targetId = `cite-ref-${messageIndex}-${key}`;
            const footnoteTranslation = translations[footnoteKey];
            const displayText = footnoteTranslation?.showingTranslated ? footnoteTranslation.translatedText : cleanSnippet(value.snippet);
            
            return (
              <li key={key} id={`fn-${messageIndex}-${key}`} className="flex items-start">
                <div className="flex-grow">
                  <span className="font-semibold">{key}. {value.sourceName}:</span>
                  <em className="ml-1">"{displayText}"</em>
                  {/* ✅ FIX #4: Added onClick handler to the back-link for smooth scrolling */}
                  <a 
                    href={`#${targetId}`} 
                    onClick={(e) => handleBacklinkClick(e, targetId)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    aria-label={`Scroll back to citation ${key}`}
                  >
                    ↩︎
                  </a>
                </div>
                {translationEnabled && (
                  <div className="ml-2 flex-shrink-0">
                    {footnoteTranslation?.translated ? (
                      <button
                        onClick={() => toggleFootnoteListTranslation(footnoteKey)}
                        className="p-1 rounded transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-gray-700"
                        title={footnoteTranslation.showingTranslated ? 'Show original text' : 'Show translated text'}
                      >
                        <Languages className="w-4 h-4" />
                      </button>
                    ) : (
                      <Tippy
                        content={
                          <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3" style={{ maxWidth: '250px' }}>
                            <div className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Translate to:</div>
                            <div className="max-h-40 overflow-y-auto">
                              {supportedLanguages.map((lang) => (
                                <button
                                  key={lang.code}
                                  onClick={() => handleFootnoteListTranslate(footnoteKey, cleanSnippet(value.snippet), lang.code)}
                                  disabled={footnoteTranslation?.loading}
                                  className="w-full text-left px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {lang.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        }
                        interactive={true} placement="top" trigger="click" appendTo={() => document.body}
                      >
                        <button
                          disabled={footnoteTranslation?.loading || lockedLanguageSelections[footnoteKey]}
                          className={`p-1 rounded transition-colors ${
                            (footnoteTranslation?.loading || lockedLanguageSelections[footnoteKey])
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-gray-700'
                          }`}
                          title={lockedLanguageSelections[footnoteKey] ? 'Translation locked' : 'Translate'}
                        >
                          {footnoteTranslation?.loading ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                          ) : (
                            <Languages className="w-4 h-4" />
                          )}
                        </button>
                      </Tippy>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </>
    );
  };

  return (
    <MessageMarkdownContext.Provider value={{ footnotes: msg.footnotes, handleSendMessage, onSupClick, translationEnabled }}>
      <div className="prose prose-lg max-w-none dark:prose-invert" style={{ lineHeight: '1.7' }}>
        {renderContentWithAudio(processedText)}
        {renderFootnotesList()}
      </div>
    </MessageMarkdownContext.Provider>
  );
};

export default MessageComponent;
